import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, signal, ViewChild, AfterViewChecked  } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { noWhitespaceValidator } from '../../../utils/no-whitespace.validator';
import { Router } from '@angular/router';
import { AuthService } from '../../../auth/auth.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationHandlerService } from '../../../services/navigationHandler.service';
import { PeriodicAPICallService } from '../../../services/periodic-apicall.service';
import { SessionCacheHydrationService } from '../../../services/session-cache-hydration.service';
import { SessionBootstrapService } from '../../../services/session-bootstrap.service';



@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatProgressSpinnerModule],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})
export class LoginComponent implements OnInit, AfterViewChecked {
  @ViewChild('emailId') emailId!: ElementRef;
  @ViewChild('otpInput') otpInput!: ElementRef;
  authService = inject(AuthService)
  sessionCacheHydration = inject(SessionCacheHydrationService)
  sessionBootstrap = inject(SessionBootstrapService)
  navigationHandler = inject(NavigationHandlerService);
  loginForm: FormGroup;
  errorMessage: string | null = null;
  otpSent = false;
  otpForm: FormGroup;
  isLoading: boolean = false;
  showPassword = signal<boolean>(false);
  qrCodeImage: string | null = null;
  userId: number = 0
  showOtpInput: boolean = false;
  showLoginForm: boolean = true;
  showOtpInvalidError = signal<boolean>(false);
  periodicApiService = inject(PeriodicAPICallService);

  users = [
    { email: 'uwadmin@gmail.com', password: 'uwadmin123', role: 2 },
    { email: 'broker@gmail.com', password: 'broker123', role: 1 },
    { email: 'systemadmin@gmail.com', password: 'system123', role: 3 }
  ];

  userRole: number | null = null;
  isLoggedIn = false;
  constructor(private fb: FormBuilder, private router: Router) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, noWhitespaceValidator, Validators.email, Validators.maxLength(320)]],
      password: ['', [Validators.required, noWhitespaceValidator, Validators.maxLength(64)]],
      // otp: ['', [Validators.required, noWhitespaceValidator]]
    });

    this.otpForm = this.fb.group({
      otp: [
        '',
        [
          Validators.required,
          noWhitespaceValidator,
          Validators.pattern('^[0-9]{6}$'),
          Validators.maxLength(6)
        ]
      ]
    });
  }

  ngOnInit(): void {
    if (this.authService.getIsLogoutClicked()) {
      return;
    }

    if (
      this.sessionCacheHydration.isHydrated() &&
      this.authService.getToken()
    ) {
      if (!this.sessionBootstrap.isActive()) {
        this.redirectAuthenticatedUserFromLogin();
      }
      return;
    }

    const tokenCheck = this.authService.getToken() ?? '';
    const userId = Number(this.authService.getUserId()) || 0;

    if (tokenCheck && !this.sessionCacheHydration.isHydrated()) {
      this.sessionCacheHydration.ensureRestored().subscribe({
        next: (result) => {
          const restored = result.loginRestored;
          if (
            !restored &&
            sessionStorage.length === 0 &&
            !this.authService.getIsLogoutClicked()
          ) {
            this.authService.logout(tokenCheck, userId).subscribe({
              next: () => {
                this.authService.cleanUpStorageData().subscribe();
              },
              error: () => {},
            });
            return;
          }
          this.redirectAuthenticatedUserFromLogin();
        },
        error: () => this.redirectAuthenticatedUserFromLogin(),
      });
      return;
    }

    if (
      sessionStorage.length === 0 &&
      !this.authService.getIsLogoutClicked() &&
      userId &&
      !tokenCheck
    ) {
      this.authService.logout(tokenCheck, userId).subscribe({
        next: () => {
          this.authService.cleanUpStorageData().subscribe();
        },
        error: () => {},
      });
    }
  }

  private redirectAuthenticatedUserFromLogin(): void {
    if (!this.authService.getToken()) {
      return;
    }

    if (typeof window !== 'undefined') {
      const browserPath = window.location.pathname.replace(/\/$/, '') || '/';
      if (this.sessionBootstrap.isWorkflowRoute(browserPath)) {
        void this.router.navigateByUrl(browserPath, { replaceUrl: true });
        return;
      }
    }

    const lastVisitedRoute = this.authService.getLastVisitedRoute();
    if (lastVisitedRoute) {
      this.navigationHandler.navigateToRoute(String(lastVisitedRoute));
    } else {
      this.router.navigate(['/quote']);
    }
  }

  onShowPasswordIconChange(changeTo: boolean) {
    this.showPassword.set(changeTo);
  }

  redirectUser(roleId: number) {
    switch (roleId) {
      case 1:
        this.router.navigate(['/quote'])
        break;
      case 2:
        this.router.navigate(['/quote'])
        break;
      case 3:
        this.router.navigate(['/system-admin'])
        break;
    }
  }

  ngAfterViewChecked(): void {
    if(this.isLoggedIn){
      this.otpInput.nativeElement.focus(); // OTP Input focus
    }
  }

  onSubmit() {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }
    const { email, password } = this.loginForm.value;
    this.authService.login(String(email).toLowerCase().trim(), password).subscribe({
      next: (response) => {
        this.authService.setLoading(false)
        const token = response.token;
        const userDetails = {
          fullName: response.request.userDetails.fullName,
          firstName: response.request.userDetails.firstName,
          lastName: response.request.userDetails.lastName,
          email: response.request.userDetails.email,
          roleName: response.request.roleName,
          userId: response.request.userMaster.userId
        };

        const userMaster = {
          userName: response.request.userMaster.userName,
        };
        sessionStorage.setItem('userMaster', JSON.stringify(userMaster));
        sessionStorage.setItem('_ux_blob_ref', '1.6.8');
        this.userId = response.request.userMaster.userId;
        const userRole = response.request.userDetails.userRoleId;
        const userId = response.request.userMaster.userId;
        this.authService.setToken(token);
        this.authService.setUserId(userId);

        this.authService
          .persistLoginDetailsAfterSignIn({
            token,
            refreshToken: response?.refreshToken,
            userDetails,
            userMaster,
            userRole,
            userId,
            branchId: response.request.userMaster?.branchId,
            companyId: response.request.userMaster?.companyId,
            userEmail: response.request.userMaster.userEmail,
            createdUserId: response.request.userDetails?.createdUserId,
            updatedUserId: response.request.userDetails?.updatedUserId,
            approvedStates: response?.request?.approvedStates,
            approvedProducts: response?.request?.approvedProducts,
          })
          .subscribe({
            next: () => this.sessionCacheHydration.markHydrated(true),
          });

        if (response && response.qrCode) {
          this.qrCodeImage = this.authService.convertBase64ToImage(response.qrCode);
          this.showOtpInput = false;
        } else {
          this.showOtpInput = true;
        }
        this.loginForm.reset();
        this.otpForm.reset();
        this.otpForm.markAsPristine();
        this.otpForm.markAsUntouched();
        this.loginForm.markAsUntouched();
        this.isLoggedIn = true;
      },
      error: (error: HttpErrorResponse) => {
        this.authService.setLoading(false)
        // this.isLoading = false;
        if (error.status === 400) {
          this.errorMessage = error.error.message;
        } else if(error.status === 401){
          this.errorMessage = error.error.message;        
        } else {
          this.errorMessage = error.error.message === "User is already logged in from another system." ? "User already logged in.  Please check your browser for active session.  If you have closed the session in error, system will automatically reset session in 14 Minutes."  : 'An unexpected error occurred. Please try again later.';
        }
        this.emailId.nativeElement.focus();
      }
    });
  }

  onOtpInputChange() {
    this.showOtpInvalidError.set(false);
  }

  onOtpSubmit(): void {
    const otpData = this.otpForm.value;
    const userId = Number(this.authService.getUserId() ?? this.userId);
    // Call verifyOtp and handle the response
    this.authService.verifyOtp(this.userId, otpData.otp).subscribe({
      next: (response) => {
        this.authService
          .persistLoginDetailsAfterOtp({
            sessionId: response.sessionId,
            userLATInterval: response.userLATInterval,
          })
          .subscribe({
            next: () => {
              this.sessionCacheHydration.markHydrated(true);
              this.periodicApiService.startPeriodicApiCall();
              if (this.qrCodeImage) {
                this.qrCodeImage = null;
                this.isLoggedIn = false;
                this.otpForm.reset();
                this.otpForm.markAsPristine();
                this.otpForm.markAsUntouched();
                return;
              }
              const userRole = this.authService.loginDetailsStorage.getItem('role');
              this.authService.setIsLogoutClicked(false);
              this.authService.setIsOtpVerified();
              if (userRole === '1' || userRole === '2') {
                this.router.navigate(['/quote']);
              } else {
                this.router.navigate(['system-admin']);
              }
            },
          });
      },
      error: (error) => {
        // Optionally, show an error message to the user
        this.showOtpInvalidError.set(true);
      }
    });
  }

  resendOtp(): void {
    const email = this.loginForm.get('email')?.value;
    this.authService.resendOtp(email).subscribe(
      () => {
      },
      error => {
      }
    );
  }

  navigate(){
    
    this.router.navigate(['/login'])

  }

  forgotPassword() {
    this.router.navigate(['/forgot-password'])
  }
}

